import { useEffect, useRef, useState } from 'react';
import type { ElementType } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { Group, Mesh } from 'three';
import type { GLTFModalProps, MeshRef, Vec3 } from '../store/types';

const AmbientLight = 'ambientLight' as unknown as ElementType;
const DirectionalLight = 'directionalLight' as unknown as ElementType;
const MeshElement = 'mesh' as unknown as ElementType;
const Primitive = 'primitive' as unknown as ElementType;

const scaleMeshUp = (meshRef: MeshRef, ratio: number): void => {
    if (!meshRef.current) return;
    meshRef.current.scale.x += ratio;
    meshRef.current.scale.y += ratio;
    meshRef.current.scale.z += ratio;
};

const scaleMeshDown = (meshRef: MeshRef, ratio: number): void => {
    if (!meshRef.current) return;
    meshRef.current.scale.x -= ratio;
    meshRef.current.scale.y -= ratio;
    meshRef.current.scale.z -= ratio;
};

const getModelConfig = (planetType: string, scale: Vec3): { maxScale: number; ratio: number } => {
    let maxScale = scale[0] * 2;
    let ratio = 0.1;

    if (planetType === "portfolio") {
        maxScale = scale[0] * 2;
        ratio = 0.01;
    } else if (planetType === "about") {
        maxScale = scale[0] * 2;
        ratio = 0.1;
    } else if (planetType === "connect") {
        maxScale = scale[0] * 2.5;
        ratio = 0.1;
    } else if (planetType === "services") {
        ratio = 1;
        maxScale = scale[0] * 2.5;
    }

    return { maxScale, ratio };
};

const GLTFModal = ({
    path,
    position,
    rotation,
    rotationSpeed,
    translate = [0, 0, 0],
    planetType,
    scale,
    animatePosition,
    goLeft,
    animatePlanet,
    instantActiveState = false,
    sat = false,
    ...rest
}: GLTFModalProps) => {
    const gltf = useGLTF(path, true) as { scene: Group };
    const mesh = useRef<Mesh | null>(null);

    const [animatePlanets, setAnimatePlanet] = useState<boolean>(animatePlanet);
    const [hidePlanet, setHidePlanet] = useState<boolean>(goLeft);

    // Keep these props accepted for compatibility with existing call sites.
    void translate;
    void sat;

    useFrame(() => {
        if (!mesh.current || !rotationSpeed) return;
        mesh.current.rotation.x += rotationSpeed[0];
        mesh.current.rotation.y += rotationSpeed[1];
        mesh.current.rotation.z += rotationSpeed[2];
    });

    useEffect(() => {
        setAnimatePlanet(animatePlanet === true);
        setHidePlanet(goLeft);
    }, [animatePlanet, goLeft]);

    useEffect(() => {
        if (!instantActiveState || !animatePlanet || !mesh.current) return;

        const { maxScale } = getModelConfig(planetType, scale);
        const targetY = planetType === "services" ? 10 : animatePosition[1];

        mesh.current.position.x = animatePosition[0];
        mesh.current.position.y = targetY;
        mesh.current.position.z = position[2];
        mesh.current.scale.x = maxScale;
        mesh.current.scale.y = maxScale;
        mesh.current.scale.z = maxScale;
    }, [animatePlanet, animatePosition, instantActiveState, planetType, position, scale]);

    useFrame(() => {
        if (!position || !mesh.current) return;
        if (window.screen.width <= 500) {
            return;
        }

        if (animatePlanets) {
            if (mesh.current.position.y > animatePosition[1] && planetType !== "services") {
                mesh.current.position.y -= 0.5;
            }
        } else {
            if (mesh.current.position.y < position[1]) {
                mesh.current.position.y += 0.5;
            }
        }

        const { maxScale, ratio } = getModelConfig(planetType, scale);

        if (animatePlanets) {
            if (mesh.current.scale.x < maxScale) scaleMeshUp(mesh, ratio);
            if (mesh.current.position.y < 10 && planetType === "services") mesh.current.position.y += 1;
        } else {
            if (mesh.current.scale.x > scale[0]) scaleMeshDown(mesh, ratio);
            if (mesh.current.position.y > 0 && planetType === "services") mesh.current.position.y -= 1;
        }

        if (animatePlanets) {
            if (mesh.current.position.x < animatePosition[0]) mesh.current.position.x += 5;
        } else {
            if (mesh.current.position.x > position[0]) mesh.current.position.x -= 5;
        }
    });

    return (
        <>
            <AmbientLight intensity={0.25} />
            <DirectionalLight position={position} castShadow />
            <MeshElement ref={mesh} castShadow {...rest} scale={scale} position={position} rotation={rotation}>
                {!hidePlanet && <Primitive object={gltf.scene} dispose={null}  />}
            </MeshElement>
        </>
    );
};

export default GLTFModal;
